import {
  format,
  isSameDay,
  isThisYear,
  isToday,
  isTomorrow,
  parseISO,
} from 'date-fns';

/**
 * Formats an event/slot datetime into a short, human-relative label.
 *
 * Components accept the RAW datetime string and format internally via this
 * helper — never pass a pre-formatted date string as a prop. Keeping the
 * formatting here (not inline in a component) means every surface that shows a
 * slot reads the same, and the relative-day logic lives in one place.
 *
 * @param datetime ISO datetime, e.g. "2025-05-06T11:30:40"
 * @returns e.g. "Today · 11:30 AM", "Tomorrow · 6:30 PM", "Fri, 27 Jun · 7:30 PM"
 */
export function formatDateTime(datetime: string): string {
  // No trailing Z → `parseISO` reads this as LOCAL time, which is what we want:
  // slots are stored in the venue's zone, not UTC.
  const when = parseISO(datetime);

  return `${relativeDay(when)} · ${format(when, 'h:mm a')}`; // e.g. "6:30 PM"
}

/** "Today" / "Tomorrow" / "Fri, 27 Jun" / "Fri, 27 Jun 2027" — the day label
 * shared by {@link formatDateTime} and {@link formatSlotSummary}. The year is
 * dropped when it's the current one, since it's implied. */
function relativeDay(d: Date): string {
  return isToday(d)
    ? 'Today'
    : isTomorrow(d)
      ? 'Tomorrow'
      : format(d, isThisYear(d) ? 'EEE d MMM' : 'EEE d MMM yyyy');
}

/**
 * A date WITH its year — "27 Jun 2025". The one case {@link formatDateTime} can't
 * serve: a date arbitrarily far BEHIND us, where a bare "27 Jun" silently reads
 * as this year. The day a membership started is the only one so far.
 *
 * @param datetime ISO datetime, e.g. "2025-06-27T00:00:00Z"
 * @returns e.g. "27 Jun 2025"
 */
export function formatDateWithYear(datetime: string): string {
  return format(parseISO(datetime), 'd MMM yyyy');
}

/**
 * A date split into the parts a layout sets SEPARATELY — the membership
 * timeline's date column (the day number set over the month) and its month
 * headings. Everything else wants one whole string; use the two formatters
 * above for those.
 *
 * @param datetime ISO datetime, e.g. "2026-02-14T00:00:00Z"
 * @returns e.g. { weekday: "Sat", day: "14", month: "Feb", monthLong: "February", year: "2026" }
 */
export function dateParts(datetime: string) {
  const at = parseISO(datetime);
  return {
    weekday: format(at, 'EEE'),
    day: format(at, 'd'),
    month: format(at, 'MMM'),
    monthLong: format(at, 'MMMM'),
    year: format(at, 'yyyy'),
  };
}

/** One end of an event's bookable run. Structural, so `ShortEvent`'s
 * `nextBookableSlot` / `lastBookableSlot` satisfy it as-is. */
type SlotEnds = { startDatetime: string };

/**
 * The one-line slot label for an event card / meta block. Collapses the whole
 * run to its ENDS, so it stays one line however many slots there are:
 *
 *   one slot            → "Fri, 7 Aug · 7:30 PM"
 *   several, one day    → "Fri, 7 Aug, Multiple slots"
 *   several, many days  → "Fri, 7 Aug – Sun, 9 Aug"
 *
 * Takes the ends rather than every slot because that's all a card HAS: a
 * `ShortEvent` carries `nextBookableSlot` / `lastBookableSlot` and no array.
 * They're the same slot when there's only one, which is how case 1 is detected.
 */
export function formatSlotSummary(first: SlotEnds, last: SlotEnds): string {
  // Both ends are the same slot ⇒ there is only one, and its exact time is
  // worth showing — relative day ("Today") and all.
  if (first.startDatetime === last.startDatetime) {
    return formatDateTime(first.startDatetime);
  }

  // Both ends read from slot STARTS: a slot running past midnight belongs to the
  // day it began on, so taking the last one's END would land the range on a day
  // that holds no slot.
  const from = parseISO(first.startDatetime);
  const to = parseISO(last.startDatetime);

  // One day ⇒ one date: "Fri – Fri" says nothing. Today/Tomorrow is fair game
  // here since, like the single-slot case above, it's one unambiguous day.
  if (isSameDay(from, to)) return `${relativeDay(from)} · Multiple slots`;

  // No "Multiple slots" tag here — a span of days can't be read as anything
  // but several slots.
  return `${relativeDay(from)} to ${relativeDay(to)}`;
}
